// MultiCcsApp.js
// -----------------------------------------------------------------------------
// Root of the MultiCCS sub-app. Owns every piece of session state and hands the
// api down as a single prop, so no child fetches anything itself.
//
// It renders inside a fixed overlay in App.js, ABOVE the main app's view tree.
// That's what keeps App.js untouched: the running <Simulation> is mounted here,
// not in App's applicationView switch, so starting a case in a block can't
// disturb whatever the main app was showing.
//
// Screens:
//   sessions  MultiSessionList   landing — In Progress | Completed
//   builder   MultiCaseBuilder   pick 2-13 cases, name, settings
//   break     MultiBreakScreen   between cases; also the pre-block screen
//   case      Simulation         the real thing, from the main app
//   review    MultiReview        sidebar of cases -> grade view
//
// The case lifecycle:
//   break -> Continue -> startCase (+ incompleteStart when resuming)
//         -> Simulation -> case ends -> fetchGrading -> record + gamify
//         -> back to break, grade hidden
//   ...until every case is done, then -> review.
//
// Props:
//   route                string                                   (required)
//   userData             login payload                            (required)
//   userProfile          login profile                            (required)
//   primaryColor         string                                   (required)
//   selectedTheme        theme object                             (required)
//   onExit               () => void — close the overlay           (required)
//   setAuthenticationError () => void — App's 401 popup           (required)
// -----------------------------------------------------------------------------

import React from "react";
import MDSpinner from "react-md-spinner";
import Simulation from "../Simulation";
import MultiSessionList from "./MultiSessionList";
import MultiCaseBuilder from "./MultiCaseBuilder";
import MultiBreakScreen from "./MultiBreakScreen";
import MultiReview from "./MultiReview";
import createMultiCcsApi, {
  CASE_STATUS,
  elapsedSeconds,
  isSessionComplete,
  nextCaseEntry,
  parseRememberedSettings,
} from "./multiCcsFetches";
import "./multiCcs.css";

class MultiCcsApp extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      screen: "sessions",

      // Case list — fetched once on mount, refreshed after each grade so
      // COMPLETED / AvgGradeForCase stay honest.
      cases: [],
      casesLoading: true,
      casesError: null,

      // Sessions
      sessions: [],
      sessionsLoading: true,
      sessionsError: null,

      // The open session (a normalized session object)
      session: null,

      // Flags
      creating: false,
      createError: null,
      starting: false,
      startError: null,
      savingSettings: false,
      renamingId: null,
      grading: false,

      // Simulation payload for the case currently being run
      simulation: null,

      // Reading options, shared with the main grading page
      readingOptions: null,

      // The user's last-used block settings, from CUSTOMER.MultiCcsSettings on
      // the login payload. A new block starts from these; saving a block or
      // changing settings mid-block writes them back.
      rememberedSettings: parseRememberedSettings(
        props.userData ? props.userData.MultiCcsSettings : "",
      ),
    };

    this.api = createMultiCcsApi({
      route: props.route,
      userData: () => this.props.userData,
      userProfile: () => this.props.userProfile,
      onAuthError: () => {
        if (this.props.setAuthenticationError) {
          this.props.setAuthenticationError();
        }
      },
    });

    this._mounted = false;
    this._controller = null;
  }

  componentDidMount() {
    this.handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", this.handleBeforeUnload);

    this._mounted = true;
    this.fetchCases();
    this.fetchSessions();
    this.fetchReadingOptions();
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    this._mounted = false;
    if (this._controller) this._controller.abort();
  }

  // ── Loading ──────────────────────────────────────────────────────────────

  fetchCases = () => {
    this.setState({ casesLoading: true, casesError: null });
    this.api
      .listCases()
      .then((cases) => {
        if (!this._mounted) return;
        this.setState({ cases: cases, casesLoading: false });
      })
      .catch((error) => {
        if (!this._mounted) return;
        if (error && error.isAuthError) {
          this.setState({ casesLoading: false });
          return;
        }
        this.setState({
          casesError:
            "The case list wouldn't load. Check your connection and try again.",
          casesLoading: false,
        });
      });
  };

  fetchSessions = () => {
    this.setState({ sessionsLoading: true, sessionsError: null });
    this.api
      .listMultiCases()
      .then((sessions) => {
        if (!this._mounted) return;
        this.setState({ sessions: sessions, sessionsLoading: false });
      })
      .catch((error) => {
        if (!this._mounted) return;
        // Same reasoning as fetchCases: don't layer a generic error message
        // on top of the 401 popup.
        if (error && error.isAuthError) {
          this.setState({ sessionsLoading: false });
          return;
        }
        this.setState({
          sessionsError:
            "Your blocks wouldn't load. Check your connection and try again.",
          sessionsLoading: false,
        });
      });
  };

  fetchReadingOptions = () => {
    this.api.getReadingOptions().then((options) => {
      if (!this._mounted || !options) return;
      this.setState({ readingOptions: options });
    });
  };

  // ── Session persistence ──────────────────────────────────────────────────

  // Single funnel for every CaseProgress write. Updates local state first so the
  // UI never waits on the network, then persists.
  //
  //   mutate(progress) -> next progress
  saveProgress(mutate, options) {
    const config = options || {};
    const session = this.state.session;
    if (!session) return Promise.resolve(null);

    const nextProgress = mutate(
      JSON.parse(JSON.stringify(session.CaseProgress)),
    );
    nextProgress.ModifiedDate = new Date().toISOString();

    const completed = isSessionComplete(nextProgress);
    const nextSession = Object.assign({}, session, {
      CaseProgress: nextProgress,
      Completed: completed,
    });

    this.setState({ session: nextSession });

    return this.api
      .saveMultiCaseProgress({
        multiCaseId: nextSession.ID,
        caseProgress: nextProgress,
        completed: completed,
      })
      .then((stored) => {
        if (!this._mounted) return null;
        // The backend fills in CurrentStateId for in-progress caseguids, so
        // prefer its copy when it hands one back.
        if (stored) {
          this.setState({ session: stored });
          return stored;
        }
        return nextSession;
      })
      .catch((error) => {
        // A failed progress write isn't fatal — the local copy is still right,
        // and the next write will carry the same blob. Surface it only when the
        // caller cares (starting a case does).
        if (config.rethrow) throw error;
        return nextSession;
      });
  }

  // ── Sessions ─────────────────────────────────────────────────────────────

  handleNewBlock = () => {
    this.setState({ screen: "builder", createError: null });
  };

  handleCreate = ({ title, caseIds, settings }) => {
    this.setState({ creating: true, createError: null });
    // Carry these settings into the next block the user builds.
    this.rememberSettings(settings);
    this.api
      .createMultiCase({ title: title, caseIds: caseIds, settings: settings })
      .then((session) => {
        if (!this._mounted) return;
        this.setState({
          creating: false,
          session: session,
          screen: "break",
        });
        this.fetchSessions();
      })
      .catch((error) => {
        if (!this._mounted) return;
        if (error && error.isAuthError) {
          this.setState({ creating: false });
          return;
        }
        this.setState({
          creating: false,
          createError:
            "The block wouldn't start. Check your connection and try again.",
        });
      });
  };

  handleOpenSession = (session) => {
    // Always refetch: the stored CurrentStateId is filled in by the backend at
    // read time, and the list copy may predate the last case that was run.
    this.setState({ starting: true, startError: null });
    this.api
      .getMultiCase(session.ID)
      .then((fresh) => {
        if (!this._mounted) return;
        const target = fresh || session;
        this.setState({
          starting: false,
          session: target,
          screen: target.Completed ? "review" : "break",
        });
      })
      .catch(() => {
        if (!this._mounted) return;
        // Fall back to the list's copy rather than blocking the user outright.
        this.setState({
          starting: false,
          session: session,
          screen: session.Completed ? "review" : "break",
        });
      });
  };

  handleRenameSession = (session, title) => {
    this.setState({ renamingId: session.ID });

    const applyLocally = (sessions) =>
      sessions.map(function (row) {
        if (row.ID !== session.ID) return row;
        return Object.assign({}, row, {
          Title: title,
          CaseProgress: Object.assign({}, row.CaseProgress, { Title: title }),
        });
      });

    this.api
      .updateMultiCaseTitle(session.ID, title)
      .then(() => {
        if (!this._mounted) return;
        this.setState(function (prevState) {
          const next = {
            renamingId: null,
            sessions: applyLocally(prevState.sessions),
          };
          if (prevState.session && prevState.session.ID === session.ID) {
            next.session = Object.assign({}, prevState.session, {
              Title: title,
              CaseProgress: Object.assign({}, prevState.session.CaseProgress, {
                Title: title,
              }),
            });
          }
          return next;
        });
      })
      .catch(() => {
        if (!this._mounted) return;
        this.setState({ renamingId: null });
      });
  };

  backToSessions = () => {
    this.setState({
      screen: "sessions",
      session: null,
      simulation: null,
      startError: null,
    });
    this.fetchSessions();
  };

  // ── Settings ─────────────────────────────────────────────────────────────

  // Remember these settings for the next block. Updates local state right away
  // so the builder reflects them without waiting on the network, then persists.
  // Fire-and-forget: the block carries its own copy, so a failed write only
  // costs the user their defaults next time.
  rememberSettings = (settings) => {
    this.setState({ rememberedSettings: settings });
    this.api.saveMultiCcsSettings(settings);
  };

  handleSettings = (nextSettings) => {
    this.setState({ savingSettings: true });
    // Changing settings mid-block also updates what the next block starts from.
    this.rememberSettings(nextSettings);
    this.saveProgress(function (progress) {
      progress.Settings = nextSettings;
      return progress;
    }).then(() => {
      if (!this._mounted) return;
      this.setState({ savingSettings: false });
    });
  };

  // ── Break countdown ──────────────────────────────────────────────────────

  handleBreakTick = (remaining) => {
    const session = this.state.session;
    if (!session) return;

    const nextProgress = Object.assign({}, session.CaseProgress, {
      BreakRemainingSeconds: remaining,
    });
    this.setState({
      session: Object.assign({}, session, { CaseProgress: nextProgress }),
    });
  };

  // ── Running a case ───────────────────────────────────────────────────────

  caseRow(caseId) {
    const cases = this.state.cases;
    for (let i = 0; i < cases.length; i++) {
      if (cases[i].ID === caseId) return cases[i];
    }
    return null;
  }

  handleContinue = () => {
    const session = this.state.session;
    if (!session) return;

    const entry = nextCaseEntry(session.CaseProgress);
    if (!entry) {
      this.setState({ screen: "review" });
      return;
    }

    const caseRow = this.caseRow(entry.CaseId);
    if (!caseRow) {
      this.setState({
        startError:
          "Case " +
          entry.CaseId +
          " isn't in your case list. Reload and try again.",
      });
      return;
    }

    const resuming =
      entry.Status === CASE_STATUS.IN_PROGRESS &&
      entry.caseguid &&
      entry.CurrentStateId !== null &&
      entry.CurrentStateId !== undefined;

    this.setState({ starting: true, startError: null });

    if (resuming) {
      this.resumeCase(entry, caseRow);
    } else {
      this.startFreshCase(entry, caseRow);
    }
  };

  startFreshCase(entry, caseRow) {
    this.api
      .startCase(caseRow)
      .then((result) => {
        if (!this._mounted) return null;

        // Record the attempt before handing over to Simulation: if the user
        // walks away mid-case, this is what lets us find it again.
        const startedAt = new Date().toISOString();
        return this.saveProgress(function (progress) {
          progress.Cases = progress.Cases.map(function (row) {
            if (row.CaseId !== entry.CaseId) return row;
            return Object.assign({}, row, {
              Status: CASE_STATUS.IN_PROGRESS,
              caseguid: result.caseguid,
              StartedAt: startedAt,
            });
          });
          progress.BreakRemainingSeconds = null;
          return progress;
        }).then(() => {
          if (!this._mounted) return null;
          this.setState({
            starting: false,
            screen: "case",
            simulation: {
              caseData: caseRow,
              simulationData: result.simulationData,
              startCaseData: result.startCaseData,
              caseguid: result.caseguid,
              caseId: entry.CaseId,
              startedAt: startedAt,
            },
          });
          return null;
        });
      })
      .catch((error) => {
        if (!this._mounted) return;
        if (error && error.isAuthError) {
          this.setState({ starting: false });
          return;
        }
        this.setState({
          starting: false,
          startError:
            error && error.isCaseListError
              ? String(error.message).replace("Error: ", "")
              : "That case wouldn't start. Check your connection and try again.",
        });
      });
  }

  // Resuming needs BOTH endpoints: /startcase.webapi issues the caseguid that
  // /incompletestart.webapi wants, and the stored CurrentStateId picks out which
  // saved attempt to restore (the ialist can also hold attempts made outside
  // this block, so we match rather than take the newest).
  resumeCase(entry, caseRow) {
    this.api
      .startCase(caseRow)
      .then((result) => {
        if (!this._mounted) return null;

        const match = result.ialist.filter(function (attempt) {
          return attempt.CurrentStateId === entry.CurrentStateId;
        })[0];

        // The stored attempt is gone — start the case over rather than
        // resuming into someone else's state.
        if (!match) {
          const startedAt = new Date().toISOString();
          return this.saveProgress(function (progress) {
            progress.Cases = progress.Cases.map(function (row) {
              if (row.CaseId !== entry.CaseId) return row;
              return Object.assign({}, row, {
                Status: CASE_STATUS.IN_PROGRESS,
                caseguid: result.caseguid,
                CurrentStateId: null,
                StartedAt: startedAt,
              });
            });
            progress.BreakRemainingSeconds = null;
            return progress;
          }).then(() => {
            if (!this._mounted) return null;
            this.setState({
              starting: false,
              screen: "case",
              simulation: {
                caseData: caseRow,
                simulationData: result.simulationData,
                startCaseData: result.startCaseData,
                caseguid: result.caseguid,
                caseId: entry.CaseId,
                startedAt: startedAt,
              },
            });
            return null;
          });
        }

        return this.api
          .incompleteStart({
            caseguid: result.caseguid,
            currentStateId: match.CurrentStateId,
            simulationData: result.simulationData,
          })
          .then((resumed) => {
            if (!this._mounted) return null;
            return this.saveProgress(function (progress) {
              progress.Cases = progress.Cases.map(function (row) {
                if (row.CaseId !== entry.CaseId) return row;
                return Object.assign({}, row, {
                  caseguid: result.caseguid,
                });
              });
              progress.BreakRemainingSeconds = null;
              return progress;
            }).then(() => {
              if (!this._mounted) return null;
              this.setState({
                starting: false,
                screen: "case",
                simulation: {
                  caseData: caseRow,
                  simulationData: resumed.simulationData,
                  startCaseData: resumed.startCaseData,
                  caseguid: result.caseguid,
                  caseId: entry.CaseId,
                  // Elapsed time picks up from the original start, not now.
                  startedAt: entry.StartedAt || new Date().toISOString(),
                },
              });
              return null;
            });
          });
      })
      .catch((error) => {
        if (!this._mounted) return;
        if (error && error.isAuthError) {
          this.setState({ starting: false });
          return;
        }
        this.setState({
          starting: false,
          startError:
            error && error.isCaseListError
              ? String(error.message).replace("Error: ", "")
              : "That case wouldn't resume. Check your connection and try again.",
        });
      });
  }

  // Simulation calls this when the case ends — from the 2-minute case-end timer
  // or from Confirm Orders. It's the same prop the main app passes, so the
  // component itself needs no changes.
  //
  // Unlike App's version: no navigation to the grading screen, and the grade is
  // never shown. We bank it and go back to the break screen.
  handleGradeCase = () => {
    const simulation = this.state.simulation;
    if (!simulation) return Promise.resolve(null);
    // Simulation can fire this twice (timer + confirm); ignore the second.
    if (this.state.grading) return Promise.resolve(null);

    this.setState({ grading: true });

    return this.api
      .gradeCase({
        caseguid: simulation.caseguid,
        caseId: simulation.caseId,
      })
      .then((grading) => {
        if (!this._mounted) return null;

        const completedAt = new Date().toISOString();
        const total =
          grading && typeof grading.TotalGrade === "number"
            ? grading.TotalGrade
            : null;

        return this.saveProgress(function (progress) {
          progress.Cases = progress.Cases.map(function (row) {
            if (row.CaseId !== simulation.caseId) return row;
            return Object.assign({}, row, {
              Status: CASE_STATUS.COMPLETED,
              caseguid: simulation.caseguid,
              CurrentStateId: null,
              GradeForCase: total,
              CompletedAt: completedAt,
              ElapsedSeconds: elapsedSeconds(simulation.startedAt, completedAt),
            });
          });
          // A fresh break for the next case.
          progress.BreakRemainingSeconds = null;
          return progress;
        }).then((session) => {
          if (!this._mounted) return null;

          // Gamification fires per case, same as a single case would.
          this.api.triggerGamificationEvent([simulation.caseId], total);

          // Keep COMPLETED / AvgGradeForCase current for the builder and the
          // break screen. Fire and forget.
          this.fetchCases();

          const done = session
            ? isSessionComplete(session.CaseProgress)
            : false;
          this.setState({
            grading: false,
            simulation: null,
            screen: done ? "review" : "break",
          });
          return grading;
        });
      })
      .catch((error) => {
        if (!this._mounted) return null;
        // The case is over either way — don't strand the user in a dead
        // Simulation. Send them back to the break screen; the case stays
        // in_progress and can be resumed.
        if (error && error.isAuthError) {
          this.setState({
            grading: false,
            simulation: null,
            screen: "break",
          });
          return null;
        }
        this.setState({
          grading: false,
          simulation: null,
          screen: "break",
          startError:
            "That case ended, but its grade wouldn't save. Resume it to try again.",
        });
        return null;
      });
  };

  // Simulation's "Exit Case" -> confirmExitCase popup -> Yes. The backend has
  // already been recording CurrentStateId as orders went in; the next
  // getMultiCase fills it into the blob for us.
  handleExitCase = () => {
    const session = this.state.session;
    if (!session) {
      this.backToSessions();
      return;
    }

    this.setState({ simulation: null, screen: "break" });

    this.api
      .getMultiCase(session.ID)
      .then((fresh) => {
        if (!this._mounted || !fresh) return;
        this.setState({ session: fresh });
      })
      .catch(function () {
        // The local copy still says in_progress with the right caseguid, so a
        // failure here only costs us the CurrentStateId until the next read.
      });
  };

  // Simulation needs this for its location popups. It mutates simulationData in
  // place in App; do the same here against our copy.
  updateLocation = (id) => {
    const simulation = this.state.simulation;
    if (!simulation) return;

    const simulationData = Object.assign({}, simulation.simulationData);
    simulationData.CaseLocationId = id;
    switch (id) {
      case 1:
        simulationData.CaseLocationDesc = "Emergency Department";
        break;
      case 2:
        simulationData.CaseLocationDesc = "Office";
        break;
      case 3:
        simulationData.CaseLocationDesc = "Intensive Care Unit";
        break;
      case 4:
        simulationData.CaseLocationDesc = "Home";
        break;
      case 5:
        simulationData.CaseLocationDesc = "Inpatient Unit";
        break;
      default:
        simulationData.CaseLocationDesc = "";
    }

    this.setState({
      simulation: Object.assign({}, simulation, {
        simulationData: simulationData,
      }),
    });
  };

  // ── Review-later, shared between the grade view and the case list ─────────

  handleReviewLater = (caseId, marked) => {
    this.setState(function (prevState) {
      return {
        cases: prevState.cases.map(function (row) {
          if (row.ID !== caseId) return row;
          return Object.assign({}, row, { ReviewLaterFlag: marked });
        }),
      };
    });
  };

  handleReadingOptions = (next) => {
    this.setState({ readingOptions: next });
    this.api.saveReadingOptions(next);
  };

  reviewLaterIds() {
    return this.state.cases
      .filter(function (row) {
        return !!row.ReviewLaterFlag;
      })
      .map(function (row) {
        return row.ID;
      });
  }

  // ── Render ───────────────────────────────────────────────────────────────

  renderSimulation() {
    const simulation = this.state.simulation;
    const session = this.state.session;
    const settings = session.CaseProgress.Settings;

    // Simulation reads timedExam / customTimeLimit exactly the way the main app
    // feeds them, so the block's settings map straight onto its own timer.
    const timedExam = !!settings.EnforceCaseTimeLimit;

    return (
      <div className="mccs-sim">
        <Simulation
          key={simulation.caseguid}
          caseData={simulation.caseData}
          simulationData={simulation.simulationData}
          startCaseData={simulation.startCaseData}
          userData={this.props.userData}
          userProfile={this.props.userProfile}
          route={this.props.route}
          primaryColor={this.props.primaryColor}
          timedExam={timedExam}
          customTimeLimit={settings.CustomTimeLimit}
          networkLag={settings.NetworkLag}
          fetchOutstanding={false}
          updateLocation={this.updateLocation}
          fetchGrading={this.handleGradeCase}
          triggerGamificationEvent={() => {
            // Gamification is fired once per case from handleGradeCase, after
            // the grade is banked. ReviewOrders would otherwise fire it early
            // and we'd double-count.
            return Promise.resolve(null);
          }}
          closeGrading={this.handleExitCase}
          setAuthenticationError={this.props.setAuthenticationError}
        />
      </div>
    );
  }

  render() {
    const {
      screen,
      session,
      cases,
      casesLoading,
      casesError,
      sessions,
      sessionsLoading,
      sessionsError,
      simulation,
    } = this.state;

    let body;

    if (screen === "case" && simulation && session) {
      body = this.renderSimulation();
    } else if (screen === "builder") {
      body = (
        <MultiCaseBuilder
          cases={cases}
          loading={casesLoading}
          error={casesError}
          creating={this.state.creating}
          createError={this.state.createError}
          initialSettings={this.state.rememberedSettings}
          onCreate={this.handleCreate}
          onCancel={this.backToSessions}
          onRetry={this.fetchCases}
        />
      );
    } else if (screen === "break" && session) {
      // The case list is needed for titles and time limits.
      if (casesLoading) {
        body = (
          <div className="mccs-loading">
            <MDSpinner size={50} singleColor={this.props.primaryColor} />
            <p>Loading cases...</p>
          </div>
        );
      } else {
        body = (
          <MultiBreakScreen
            session={session}
            cases={cases}
            nextEntry={nextCaseEntry(session.CaseProgress)}
            starting={this.state.starting || this.state.grading}
            startError={this.state.startError}
            savingSettings={this.state.savingSettings}
            onContinue={this.handleContinue}
            onTick={this.handleBreakTick}
            onSettings={this.handleSettings}
            onExit={this.backToSessions}
          />
        );
      }
    } else if (screen === "review" && session) {
      body = (
        <MultiReview
          session={session}
          cases={cases}
          api={this.api}
          userData={this.props.userData}
          userProfile={this.props.userProfile}
          reviewLaterIds={this.reviewLaterIds()}
          onReviewLater={this.handleReviewLater}
          readingOptions={this.state.readingOptions}
          onReadingOptions={this.handleReadingOptions}
          onExit={this.backToSessions}
          primaryColor={this.props.primaryColor}
          selectedTheme={this.props.selectedTheme}
        />
      );
    } else {
      body = (
        <MultiSessionList
          sessions={sessions}
          loading={sessionsLoading}
          error={sessionsError}
          renamingId={this.state.renamingId}
          onNewBlock={this.handleNewBlock}
          onOpenSession={this.handleOpenSession}
          onRenameSession={this.handleRenameSession}
          onRetry={this.fetchSessions}
          onExit={this.props.onExit}
        />
      );
    }

    return <div className="mccs-root">{body}</div>;
  }
}

export default MultiCcsApp;
